// ─── Cluster H — Auth + Billing (the knot) ──────────────────────────────────
// Per docs/design/app-js-split-plan-260522.md §2.4 (Knot analysis), §3 Module 9
// row, §4.2 Module 9 detail block, and §4 Cleanly-Splittable Verdict.
//
// Third .ts module under web/static/spa/ (after toggles.ts in Phase 8 and
// renderers.ts in Phase 10). Compiled to ./auth-and-billing.js by
// `tsc -p tsconfig.emit.json`; the browser imports `./auth-and-billing.js`
// (hard-coded path). The .ts source is canonical; the .js sibling is a
// derived artifact (gitignored).
//
// **STRICT MODE** (user-ratified decision A, 2026-05-22). This module
// type-checks under `--strict`, which is enforced by the separate config
// `tsconfig.auth.emit.json`. The rest of spa/ stays loose for now — see
// docs/design/app-js-split-plan-260522.md §4.2 Module 9 detail block.
//
// **CLASS SHAPE** (user-ratified decision D). This module INTENTIONALLY
// introduces the first class under spa/ — `AuthClient`. Per CLAUDE.md §11a
// (class-vs-module shape) and docs/dev/CODE-STYLE.md §1a: a state-bearing
// cluster (≥2 mutable module-private vars shared across exported functions,
// SM, long-lived cache/handle) is the canonical site for class
// encapsulation. The 6-state SM + 5 user-state scalars + Supabase client
// handle + the on-authenticated callback list cleanly fits the shape;
// procedural style would leave seven scattered `let` bindings the way
// app.js currently has them. Earlier phases (toggles, renderers) stayed
// procedural because they had at most one piece of cached state each.
//
// Exports (public API surface):
//   AuthClient                                                          (class)
//   AuthState                                                  (discriminated union)
//   OAuthProvider                          (string-literal union: google|github|microsoft)
//   getAuthHeaders                                                   (singleton helper)
//   signInWithProvider(provider)                                  (DRY'd from 3 wrappers)
//   signInWithGoogle / signInWithGithub / signInWithMicrosoft   (thin wrappers — public surface)
//   signOut / logout                                                   (terminal-state)
//   updateUserBar(user)                          (reveals #user-bar — both auth paths)
//   checkSupabaseAuth                                                       (page-load boot)
//   linkInstitution                                          (POST /api/auth/link-institution)
//   setupDevBtn(id, persona)                                    (TEST_MODE dev-login wiring)
//   runTokenPreflight(valid)                              (renamed from _runTokenPreflight)
//   computeDrawOrder                                            (multi-group draw resolver)
//   canCoverWithDrawOrder(needed)                              (sufficiency check helper)
//   computeAggregateBalance(groups, fallbackUser)                 (sum across draw chain)
//   getAuthProfile / getUserClass / getUserGroups                            (accessors)
//   registerOnAuthenticated(callback)                            (IoC bridge to init.mjs)
//
// Imports from sibling split modules:
//   globals-boot.mjs → API, authFetch, esc
//   (toggles.ts is consumed via window globals during dual-state — see below)
//
// Globals (read at call time — typed via globals.d.ts):
//   window.adatool.supabase — Supabase client factory + getAuthHeaders + setHandoffToken
//   window.adatool.pricing  — pricing constants from /static/pricing.js
//   window.adatool.log      — logging
//
// **WINDOW SIDE-EFFECTS** (user-ratified decision C — retirement deferred to
// Phase 11 with the HTML entry-swap). AuthClient.boot() writes:
//   window.__adatoolUserState      — read-only accessors for cross-page nav
// These mirror app.js's current side-effects 1:1 so dual-state coexists.
//
// **DUAL-STATE NOTE.** Until Phase 11 swaps the HTML entry, app.js still
// carries copies of every auth/billing function (the back-compat path that
// runs in production). This module is a parallel typed extract — it is
// type-checked + emitted to .js so seam + property tests can pin its API,
// but NO current production code path imports it. Phase 11 swaps index.html
// to spa/init.mjs and removes the app.js copies.
//
// See:
//   docs/design/app-js-split-plan-260522.md (master plan + Module 9 detail)
//   docs/product-policy/external/customer-model-token-vs-unlimited.md §5 P-5 (draw order)
//   CLAUDE.md §11a + docs/dev/CODE-STYLE.md §1a (class-vs-module shape rule)
import { API, authFetch, esc as _escMaybe } from './globals-boot.mjs';
// UserGroupId is now used via auth-and-billing-logic.ts (which imports it directly).
import { UNLIMITED_SENTINEL, availableInGroup, computeAggregateBalance, computeDrawOrder, canCoverWithDrawOrder, isUnlimitedGroup, userIsUnlimited, } from './auth-and-billing-logic.js';
export { UNLIMITED_SENTINEL, availableInGroup, computeAggregateBalance, computeDrawOrder, canCoverWithDrawOrder, isUnlimitedGroup, userIsUnlimited, };
/**
 * Local typed wrapper for `esc` — globals-boot.mjs re-exports `window.adatool.esc`
 * which is declared optional in globals.d.ts. Under strict-mode tsc the
 * import is `((s: string) => string) | undefined`. Production `esc.js`
 * always defines `window.adatool.esc` synchronously before app.js loads, so the
 * runtime value is never undefined; fall back to a passthrough for safety
 * (and so seam tests that don't stub adatool.esc don't crash on `esc(x)`).
 */
const esc = _escMaybe || ((s) => String(s));
// ─── prod-17 redirect-origin diagnosability guard ───────────────────────────
// `signInWithProvider` asks Supabase to send the user back to
// `window.location.origin`. Supabase only honors that value if the origin is
// in its Auth → URL Configuration → Redirect-URLs allowlist; otherwise it
// SILENTLY falls back to the project Site URL (prod) — the exact staging bug
// (prod-17, 260629). That bounce is invisible from the client. This predicate
// mirrors the operator checklist in docs/ops/supabase-redirect-allowlist.md so
// a sign-in from an un-allowlisted origin can be logged loudly, turning the
// silent wrong-redirect into a diagnosable one. It does NOT block sign-in (the
// browser cannot know the true Supabase allowlist — only the dashboard does);
// it only surfaces the likely-misconfig. Keep this list in sync with that doc.
/** Canonical production origin — single source of truth for KNOWN_SUPABASE_REDIRECT_ORIGINS. */
export const PROD_ORIGIN = 'https://scholaccess.com'; // noqa: no-hardcoded-domain — canonical definition site; all other refs use this const
export const KNOWN_SUPABASE_REDIRECT_ORIGINS = Object.freeze([
    PROD_ORIGIN,
    'https://adatool-web-staging-xmfm37shjq-uc.a.run.app',
    'https://adatool-web-staging-252097544507.us-central1.run.app',
    'http://localhost:8080',
]);
/**
 * True when `origin` is a redirect origin we KNOW is (or should be) in the
 * Supabase allowlist. A `false` result means a staging-style redirect bounce
 * to the Site URL is likely — the caller logs a diagnostic warning.
 */
export function isRedirectOriginAllowlisted(origin) {
    return KNOWN_SUPABASE_REDIRECT_ORIGINS.includes(origin);
}
const _AUTH_TRANSITIONS = Object.freeze({
    SignedOut: ['Authenticating', 'LoadingProfile'],
    Authenticating: ['LoadingProfile', 'Error', 'SignedOut'],
    LoadingProfile: ['SignedIn', 'Error', 'SignedOut'],
    SignedIn: ['SigningOut', 'LoadingProfile', 'SignedOut'],
    SigningOut: ['SignedOut'],
    Error: ['Authenticating', 'SignedOut'],
});
/**
 * Exhaustiveness helper. Used inside switch-on-`state.kind` blocks: pass
 * the fall-through case to `assertNever(state)` and the call type-errors
 * if a new state was added without a handler. Pure compile-time guard;
 * at runtime it throws so an unexpected state surfaces loudly.
 */
function assertNever(x) {
    throw new Error(`unreachable: unexpected auth state ${JSON.stringify(x)}`);
}
// Helpers for typed window access — keeps the unsafe `as unknown as` casts
// localised to one place instead of sprinkled through every call site.
function _supabase() {
    return window.adatool?.supabase ?? null;
}
function _pricing() {
    return window.adatool?.pricing ?? {};
}
function _logger() {
    return window.adatool?.log ?? { info: () => { }, warn: () => { }, error: () => { } };
}
let _tokenPreflightModulePromise = null;
async function _ensureTokenPreflightModule() {
    if (_tokenPreflightModulePromise === null) {
        // Best-effort lazy load of the page-count helper. The path is a runtime
        // string the browser resolves directly — the .js sibling is browser-
        // served by FastAPI at /static/, not part of the spa/ tsconfig include
        // set, so it is opaque to TypeScript's static module graph.
        //
        // WHY a plain dynamic import() and NOT `new Function('p','return import(p)')`:
        // the SPA is served under a strict CSP (`script-src 'self'`, NO
        // 'unsafe-eval'). `new Function(...)` is eval-class and throws a
        // *synchronous* EvalError under that CSP — which escapes the `.catch()`
        // (the catch only sees promise rejections, not the throw during Function
        // construction). That synchronous throw rejected runTokenPreflight, which
        // is awaited OUTSIDE the upload try-block (upload.mjs), silently aborting
        // EVERY anonymous upload (the served preflight module is also CJS-emitted,
        // so even a clean import() rejects with `exports is not defined`). A native
        // dynamic import() is NOT eval-class, so CSP permits it; wrapping the whole
        // load in an async try/catch turns BOTH the synchronous (CSP) and the
        // asynchronous (module-eval) failure into a clean null, so the upload
        // proceeds — the server-side token check is authoritative; this is UX only.
        // RCA: docs/design/anon-upload-preflight-csp-eval-rca-260628.md.
        const path = '/static/upload-page-count.js';
        _tokenPreflightModulePromise = (async () => {
            try {
                return await import(/* @vite-ignore */ path); // noqa: no-inline-imports — intentional best-effort runtime import of an optional preflight module served at /static/; not part of the spa/ build graph
            }
            catch (err) {
                _logger().warn('[token-preflight] module load failed; skipping preflight:', err);
                _tokenPreflightModulePromise = null;
                return null;
            }
        })();
    }
    return _tokenPreflightModulePromise;
}
// ─── DI-friendly logger / DOM helpers ────────────────────────────────────────
function _getEl(id) {
    return document.getElementById(id); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
}
// Production render hook for the AuthClient state machine. The `#oauth-login`
// panel ships `class="oauth-login hidden"` and is the SIGNED-OUT entry point
// (Google/GitHub/Microsoft/email login buttons). It must be REVEALED while the
// user is signed-out and HIDDEN once an identity is present.
//
// WHY this exists: `transition()` calls `this._renderHook(next)` on every state
// change, but the default `_renderHook` is a no-op (() => {}). Production
// (`_client_()`) constructed `new AuthClient()` with NO renderHook, so nothing
// ever removed `hidden` from `#oauth-login` — the login buttons could never
// appear and only the "Get Started" CTA showed (staging bug 260629). Wiring
// this hook restores the reveal.
//
// The hook is ALSO the single canonical `#user-bar` reveal point. Both auth
// paths funnel through `transition({ kind: 'SignedIn' })`: the Supabase path
// via `onAuthStateChange` → `fetchAuthProfile()`, and the COOKIE/dev-login path
// via `checkSupabaseAuth()` (no session) → `fetchAuthProfile()` → `/api/auth/me`.
// Before this wiring `updateUserBar()` was never called on the cookie path, so
// `#user-bar` stayed `hidden` and the signed-in identity + Sign-out link were
// invisible (J4 journey defect 260629). Driving `updateUserBar` from the hook
// covers both paths fix-once.
export function _defaultAuthRenderHook(state) {
    const panel = _getEl('oauth-login');
    // Signed-out is the only state where the login panel should be visible.
    // Every other state (authenticating / loading / signed-in / signing-out /
    // error) keeps it hidden — the user is either mid-flow or already in.
    if (panel)
        panel.classList.toggle('hidden', state.kind !== 'SignedOut');
    if (state.kind === 'SignedIn') {
        // Reveal the user bar + write the identity/Account/History/Sign-out
        // markup from the /api/auth/me user. `updateUserBar` is no-op-safe when
        // `#user-bar` is absent and tolerates an undefined user.
        updateUserBar(state.profile.user);
    }
    else if (state.kind === 'SignedOut') {
        // Symmetric hide: a forced logout / failed session must re-conceal the
        // bar. `updateUserBar` only ever reveals, so do the hide here.
        _getEl('user-bar')?.classList.add('hidden');
    }
}
function _formatCreditsBadge(credits) {
    if (credits == null || credits < 0)
        return null;
    const P = _pricing();
    const officePages = Math.floor(credits / (P.officeCreditsPerPage ?? 1));
    const pdfPages = Math.floor(credits / (P.pdfCreditsPerPage ?? 2));
    return `${credits} credits (≈ ${officePages} Office pages / ${pdfPages} PDF pages)`;
}
// ─── AuthClient — the class wrapper around Supabase + AuthState SM ──────────
//
// Decision (D, 2026-05-22): introduce a class. The 6-state SM + 5 mutable
// user-state scalars + Supabase handle + on-authenticated callback list
// matches the canonical "state-bearing cluster" definition in
// docs/dev/CODE-STYLE.md §1a. Procedural style would scatter the scalars
// across 7 `let` bindings the way app.js currently does — which is exactly
// the smell §1a names. See CLAUDE.md §11a for the rule + this commit's
// design-doc reference for the call.
export class AuthClient {
    _state = { kind: 'SignedOut' };
    _seq = 0; // monotonic counter for race-safe profile fetch
    _profile = null;
    _userClass = 'anonymous';
    _userInstitutionName = '';
    _userTokenBalance = { credits: null };
    _userGroups = [];
    _sb;
    _onAuthenticated = [];
    // Hooks for external UI render — injectable for tests, defaults to no-op.
    // app.js calls _renderAuthUI directly inside _transitionAuth; we keep that
    // hook external so this class stays DOM-free for seam tests.
    _renderHook = () => { };
    constructor(opts) {
        if (opts && Object.prototype.hasOwnProperty.call(opts, 'client')) {
            this._sb = opts.client ?? null;
        }
        else {
            const supa = _supabase();
            this._sb = supa ? supa.getClient() : null;
        }
        if (opts?.renderHook)
            this._renderHook = opts.renderHook;
    }
    // ─── State accessors ───────────────────────────────────────────────────
    getState() { return this._state; }
    getAuthProfile() { return this._profile; }
    getUserClass() { return this._userClass; }
    getUserInstitutionName() { return this._userInstitutionName; }
    getUserTokenBalance() { return { ...this._userTokenBalance }; }
    getUserGroups() { return this._userGroups.slice(); }
    /**
     * True iff this user should bypass the client credit-sufficiency preflight.
     * Robust against the empty-`groups[]` path: /api/auth/me carries
     * `usage.is_unlimited` but NOT `groups[]`, so `_userGroups` is empty on the
     * main SPA and the top-level usage flag (held in `_profile.usage`) is the
     * only unlimited signal that survives. Also honors an unlimited group if the
     * group list DID populate (the /api/account-fed path). Delegates to the pure
     * `userIsUnlimited` helper (single source of truth). See
     * docs/rca/purdue-institutional-upload-paywall-260824.md §"v2 addendum".
     */
    isUnlimitedUser() {
        return userIsUnlimited(this._profile?.usage, this._userGroups);
    }
    /**
     * Test-side hook (mirrors app.js's window.__adatoolUserState.setForTest).
     * Primes the v2.1 group state without round-tripping /api/auth/me.
     */
    setForTest(cls, balance, groups) {
        this._userClass = cls ?? 'individual';
        this._userTokenBalance = balance ?? { credits: null };
        this._userGroups = Array.isArray(groups) ? groups : [];
    }
    /**
     * Test-side hook: prime the client from a FULL /api/auth/me-shaped profile,
     * mirroring the production `SignedIn` data flow (`_profile` + the derived
     * group/balance state) WITHOUT round-tripping the network or the Supabase
     * SM. Unlike `setForTest`, this sets `_profile`, so `isUnlimitedUser()` can
     * read `usage.is_unlimited` — letting seam tests reproduce the real preflight
     * decision for the empty-`groups[]` /api/auth/me shape where only the usage
     * flag carries the unlimited signal.
     */
    primeProfileForTest(profile) {
        this._profile = profile;
        this._syncUserStateFromProfile(profile);
    }
    // ─── State transition guard ────────────────────────────────────────────
    /**
     * Apply a transition. Returns true if accepted, false if rejected (and
     * logs the rejection). "Any state → SignedOut" is always allowed
     * (forced logout). Fires the render hook + on-authenticated callbacks
     * as appropriate.
     */
    transition(next) {
        const prev = this._state;
        const allowed = next.kind === 'SignedOut' || _AUTH_TRANSITIONS[prev.kind].includes(next.kind);
        if (!allowed) {
            _logger().warn('[auth-sm] INVALID transition:', prev.kind, '→', next.kind, '(blocked)');
            return false;
        }
        this._state = next;
        _logger().info('[auth-sm]', prev.kind, '→', next.kind);
        // Exhaustive switch — TS catches a missing case via assertNever.
        switch (next.kind) {
            case 'SignedOut':
                break;
            case 'Authenticating':
                break;
            case 'LoadingProfile':
                break;
            case 'SignedIn':
                this._profile = next.profile;
                this._syncUserStateFromProfile(next.profile);
                for (const cb of this._onAuthenticated) {
                    try {
                        cb(next.profile);
                    }
                    catch (err) {
                        _logger().warn('[auth-sm] on-authenticated callback threw:', err);
                    }
                }
                break;
            case 'SigningOut':
                break;
            case 'Error':
                break;
            default:
                assertNever(next);
        }
        this._renderHook(next);
        return true;
    }
    _syncUserStateFromProfile(data) {
        const u = data.user ?? {};
        const usage = data.usage ?? {};
        this._userClass = u.user_class ?? (usage.is_unlimited ? 'institutional' : 'individual');
        this._userInstitutionName = (data.institution?.name) ?? '';
        this._userGroups = Array.isArray(data.groups) ? data.groups.slice() : [];
        this._userTokenBalance = computeAggregateBalance(this._userGroups, u);
    }
    // ─── Public auth operations ────────────────────────────────────────────
    async getAuthHeaders() {
        const supa = _supabase();
        if (!supa)
            return {};
        return supa.getAuthHeaders();
    }
    /** Bound to OAuth button click. */
    async signInWithProvider(provider) {
        if (!this._sb) {
            _logger().error('Supabase not loaded');
            return;
        }
        if (!this.transition({ kind: 'Authenticating', provider }))
            return;
        // Per-provider options. queryParams.prompt='select_account' is only
        // honored by Google + Microsoft; GitHub ignores it. See app.js inline
        // notes / docs/design/localhost-oauth-design-260512.md predicate P1.
        const supabaseProvider = provider === 'microsoft' ? 'azure' : provider;
        const redirectOrigin = window.location.origin;
        // prod-17 guard: if this origin is not one we know is allowlisted in
        // Supabase, the OAuth round-trip will likely bounce to the Site URL
        // (prod) instead of returning here. Log it loudly so the misconfig is
        // diagnosable rather than silent. See docs/ops/supabase-redirect-allowlist.md.
        if (!isRedirectOriginAllowlisted(redirectOrigin)) {
            _logger().warn(`[auth-sm] redirect origin "${redirectOrigin}" is NOT in the known Supabase Redirect-URL allowlist ` +
                `— OAuth sign-in may bounce to the project Site URL instead of returning here. ` +
                `Add "${redirectOrigin}/**" to Supabase → Auth → URL Configuration → Redirect URLs ` +
                `(docs/ops/supabase-redirect-allowlist.md).`);
        }
        const options = {
            redirectTo: redirectOrigin,
        };
        if (provider === 'google' || provider === 'microsoft') {
            options.queryParams = { prompt: 'select_account' };
        }
        if (provider === 'microsoft') {
            options.scopes = 'email profile openid';
        }
        const { error } = await this._sb.auth.signInWithOAuth({ provider: supabaseProvider, options });
        if (error) {
            _logger().error(`${provider} sign-in failed:`, error);
            // eslint-disable-next-line @typescript-eslint/no-base-to-string -- error is a Supabase error object; .message fallback to error itself is intentional; String() wraps it
            this.transition({ kind: 'Error', reason: String(error.message ?? error) });
        }
    }
    async signOut() {
        this.transition({ kind: 'SigningOut' });
        if (this._sb)
            await this._sb.auth.signOut({ scope: 'global' });
        await fetch(`${API}/api/auth/logout`, { method: 'POST' }); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        // Wrapped: Safari private mode throws SecurityError on localStorage.
        try {
            localStorage.removeItem('_dev_access_token');
        } // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        catch (e) {
            _logger().warn('localStorage unavailable:', e);
        }
        try {
            for (const key of Object.keys(localStorage)) {
                if (key.startsWith('sb-') || key.includes('supabase')) {
                    localStorage.removeItem(key); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
                }
            }
        }
        catch (e) {
            _logger().warn('localStorage unavailable:', e);
        }
        this.transition({ kind: 'SignedOut' });
        window.location.reload();
    }
    /**
     * Fetch /api/auth/me with a sequence guard against races between
     * concurrent onAuthStateChange events. Returns silently on stale
     * responses (a newer auth op started during the await).
     */
    async fetchAuthProfile() {
        const seq = ++this._seq;
        if (!this.transition({ kind: 'LoadingProfile' })) {
            // Reject was logged; bail without firing the fetch.
            return;
        }
        try {
            const authHdrs = await this.getAuthHeaders();
            const resp = await fetch(`${API}/api/auth/me`, { headers: authHdrs }); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            if (this._seq !== seq) {
                _logger().info('[auth-sm] stale /api/auth/me response (seq', seq, 'vs current', this._seq, ') — dropped');
                return;
            }
            if (!resp.ok) {
                if (resp.status === 401)
                    this.transition({ kind: 'SignedOut' });
                else
                    this.transition({ kind: 'Error', reason: `HTTP ${resp.status}` });
                return;
            }
            const data = await resp.json();
            if (this._seq !== seq) {
                _logger().info('[auth-sm] stale /api/auth/me response (seq', seq, 'vs current', this._seq, ') — dropped');
                return;
            }
            this._profile = data;
            if (data.user)
                this.transition({ kind: 'SignedIn', profile: data });
            else
                this.transition({ kind: 'SignedOut' });
        }
        catch (err) {
            if (this._seq !== seq)
                return;
            _logger().error('[auth-sm] /api/auth/me fetch error:', err);
            this.transition({ kind: 'Error', reason: String(err?.message ?? err) });
        }
    }
    /** Boot the SM on page load + wire Supabase onAuthStateChange. */
    async checkSupabaseAuth() {
        if (!this._sb) {
            this.transition({ kind: 'SignedOut' });
            return;
        }
        const { data: { session } } = await this._sb.auth.getSession();
        if (session?.access_token) {
            await this.fetchAuthProfile();
        }
        else {
            this.transition({ kind: 'SignedOut' });
            await this.fetchAuthProfile();
        }
    }
    /** Wire Supabase auth-state-change events into the SM. */
    wireSupabaseEvents() {
        if (!this._sb)
            return;
        this._sb.auth.onAuthStateChange((event, session) => {
            _logger().info('[auth-sm] onAuthStateChange:', event, session ? 'has session' : 'no session');
            switch (event) {
                case 'INITIAL_SESSION':
                    // Reconcile against /api/auth/me on BOTH branches. A bare
                    // `transition(SignedOut)` on the no-Supabase-session branch
                    // clobbered a SignedIn state that the COOKIE/dev-login path had
                    // already established via fetchAuthProfile() — INITIAL_SESSION
                    // fires right after wireSupabaseEvents(), racing the cookie boot
                    // (J4 race 260629). /api/auth/me is the single source of truth:
                    // it honors the session cookie, so it correctly returns SignedIn
                    // for cookie/dev-login and SignedOut for a truly anonymous user.
                    void this.fetchAuthProfile(); // fire-and-forget: auth-state change handler; errors handled inside fetchAuthProfile
                    break;
                case 'SIGNED_IN':
                    if (session)
                        void this.fetchAuthProfile();
                    break;
                case 'TOKEN_REFRESHED':
                    if (session)
                        void this.fetchAuthProfile();
                    break;
                case 'SIGNED_OUT':
                    this.transition({ kind: 'SignedOut' });
                    window.location.reload();
                    break;
                case 'USER_UPDATED':
                    if (session)
                        void this.fetchAuthProfile();
                    break;
                case 'PASSWORD_RECOVERY':
                    _logger().info('[auth-sm] PASSWORD_RECOVERY event — ignoring');
                    break;
                default:
                    _logger().info('[auth-sm] Unknown auth event:', event);
                    break;
            }
        });
    }
    /**
     * IoC bridge — `init.mjs` registers `loadUserJobHistory` here so the
     * auth module can trigger a job-history reload without owning that flow.
     * Callbacks fire once per transition into `SignedIn`.
     */
    onAuthenticated(cb) {
        this._onAuthenticated.push(cb);
    }
}
// ─── Singleton + module-level wrappers ──────────────────────────────────────
// Per the user-ratified DUAL-STATE pattern: production runs app.js until
// Phase 11 swaps the HTML entry. This singleton + wrappers exist so seam
// tests can pin the module-level export surface and so Phase 11's
// init.mjs can wire the singleton without touching every consumer.
let _client = null;
/** Lazy-init the singleton on first use. */
function _client_() {
    // Wire the production render hook so the signed-out login panel is revealed.
    // Without a renderHook the SM's _renderHook stays a no-op and #oauth-login
    // never loses its `hidden` class (staging bug 260629).
    _client ??= new AuthClient({ renderHook: _defaultAuthRenderHook });
    return _client;
}
/**
 * Test-only reset hook. Lets seam tests reset the singleton between cases.
 * Banned in production code by convention; not exported via a hot path.
 */
export function _resetForSeamTest() { _client = null; }
/**
 * Test-only injector. Replaces the module's singleton with a caller-built
 * AuthClient — useful when seam tests need to inject a stub SupabaseClient.
 */
export function _setClientForSeamTest(client) { _client = client; }
export async function getAuthHeaders() {
    return _client_().getAuthHeaders();
}
export async function signInWithProvider(provider) {
    return _client_().signInWithProvider(provider);
}
// Thin wrappers preserve the existing public surface (callers in init.mjs +
// any HTML onclick references). Each is a single delegation — the DRY-on-
// second-site rule (CLAUDE.md §11) is satisfied because `signInWithProvider`
// is the single implementation; these are 3 named entry points sharing it.
export async function signInWithGoogle() {
    return signInWithProvider('google');
}
export async function signInWithGithub() {
    return signInWithProvider('github');
}
export async function signInWithMicrosoft() {
    return signInWithProvider('microsoft');
}
export async function signOut() { return _client_().signOut(); }
/** Alias preserved for the existing back-compat path. */
export async function logout() { return signOut(); }
export async function checkSupabaseAuth() {
    const c = _client_();
    await c.checkSupabaseAuth();
    c.wireSupabaseEvents();
}
export function getAuthProfile() { return _client_().getAuthProfile(); }
export function getUserClass() { return _client_().getUserClass(); }
export function getUserGroups() { return _client_().getUserGroups(); }
export function getUserInstitutionName() { return _client_().getUserInstitutionName(); }
export function getUserTokenBalance() { return _client_().getUserTokenBalance(); }
export function registerOnAuthenticated(callback) {
    _client_().onAuthenticated(callback);
}
// ─── User-bar render (the SINGLE #user-bar reveal, driven by _defaultAuthRenderHook) ───
//
// Consumes the `/api/auth/me` user shape (`AuthProfile['user']`): `display_name`
// / `email` for the name and a flat `provider` field (server: routes_account.py
// `safe_user()` — sets `result["provider"] = user["oauth_provider"]`). BOTH auth
// sources (Supabase OAuth + cookie/dev-login) reach this via
// `transition({kind:'SignedIn'})` → hook → here, so the argument is always the
// /api/auth/me shape — NOT the raw Supabase user (`user_metadata`/`app_metadata`).
// The prior Supabase-shaped version was dead code (never called in production)  // noqa: dead-code-tombstone — WHY: historical J4 RCA correction note; explains why function signature changed
// and would have rendered an empty name on the cookie path (J4 RCA correction).
export function updateUserBar(user) {
    const bar = _getEl('user-bar');
    const oauthLogin = _getEl('oauth-login');
    if (!bar)
        return;
    const u = user ?? {};
    const name = (u.display_name ?? u.email) ?? '';
    const provider = typeof u.provider === 'string' ? u.provider : '';
    const providerLabel = provider
        ? ` (via ${esc(provider.charAt(0).toUpperCase() + provider.slice(1))})`
        : '';
    const barHtml = `${esc(name)}${providerLabel} · <a href="/account" class="manage-account-link">Account</a> · <a href="/history" class="manage-history-link">History</a> · <a href="#" data-action="logout">Sign out</a>`;
    bar.innerHTML = barHtml;
    bar.classList.remove('hidden');
    if (oauthLogin)
        oauthLogin.classList.add('hidden');
}
export async function linkInstitution() {
    const input = _getEl('institution-key');
    const key = input ? input.value.trim() : '';
    if (!key) {
        alert('Please enter an institution key.');
        return;
    }
    try {
        const resp = await authFetch('/api/auth/link-institution', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ key }),
        });
        const data = await resp.json();
        if (resp.ok) {
            alert('Linked to ' + data.institution);
            window.location.reload();
        }
        else {
            alert(data.detail ?? 'Failed to link institution');
        }
    }
    catch (e) {
        _logger().error('Link institution error:', e);
        alert('Failed to link institution. Please try again.');
    }
}
// ─── Dev-login button wiring (TEST_MODE) ────────────────────────────────────
export function setupDevBtn(id, persona) {
    const btn = _getEl(id);
    if (!btn)
        return;
    btn.addEventListener('click', () => {
        void (async () => {
            // dev-route-fetch-ok: user-initiated login action behind a dev-login button that is only visible when /api/version test_mode === true (init.ts) — never fires unconditionally on staging/prod
            const resp = await fetch(`${API}/api/auth/dev-login?persona=${persona}`, { method: 'POST' }); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            if (resp.ok) {
                const data = await resp.json();
                try {
                    localStorage.setItem('_dev_access_token', data.access_token);
                } // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
                catch (e) {
                    _logger().warn('localStorage unavailable:', e);
                }
                window.location.reload();
            }
        })();
    });
}
// ─── Token preflight (single-bucket draw-order resolver) ────────────────────
/** Lazy module accessor exposed for seam tests to inject a stub. */
export function _setPageCountModuleForSeamTest(mod) {
    _tokenPreflightModulePromise = mod ? Promise.resolve(mod) : null;
}
/**
 * Walk `valid` (the validated upload list) and check each file's page-
 * count against the user's draw chain. Returns the (possibly-trimmed)
 * `valid` array: files that cannot be covered by the user's quota are
 * surfaced to the user and dropped from the returned list (there is no
 * self-serve purchase path since the Stripe strike). The `| null` return
 * is retained for the caller contract (upload.mjs) but is no longer produced.
 */
export async function runTokenPreflight(valid, showBanner) {
    const c = _client_();
    // ─── Unlimited-user short-circuit (the load-bearing admit gate) ─────────
    // An unlimited user has NO finite client-side balance — the server enforces
    // the invoice/cap. Admit every file and skip the per-file credit math
    // ENTIRELY. This MUST run before the sufficiency loop. `isUnlimitedUser()`
    // honors the unlimited status via `usage.is_unlimited` (the ONLY unlimited
    // signal /api/auth/me carries — it does not emit `groups[]`, so `userGroups`
    // is empty on the main SPA) OR any unlimited group (the /api/account-fed path).
    // As of the entitlement-Epic Phase-4 re-source (260824) the server-emitted
    // top-level `credit_balance` is DISPLAY-only and CORRECT for an unlimited user
    // (server `entitlement_model.display_credit_balance` returns the unbounded
    // sentinel, not 0 — the deleted `get_credit_balances` was the D1 bug that
    // summed only non-unlimited groups → 0 → wrongly paywalled). This
    // short-circuit is retained as belt-and-suspenders; the loop below no longer
    // depends on the scalar being non-zero. Root cause + the v1 mis-fix that
    // missed this seam: docs/rca/purdue-institutional-upload-paywall-260824.md
    // §"v2 addendum".
    if (c.isUnlimitedUser())
        return valid.slice();
    const mod = await _ensureTokenPreflightModule();
    if (!mod)
        return valid;
    const P = _pricing();
    const pdfRate = P.pdfCreditsPerPage ?? 2;
    const officeRate = P.officeCreditsPerPage ?? 1;
    const userGroups = c.getUserGroups();
    const userTokenBalance = c.getUserTokenBalance();
    const survivors = [];
    for (const v of valid) {
        let info = null;
        try {
            info = await mod.countPagesForFile(v.file);
        }
        catch (e) {
            _logger().warn('[token-preflight] count failed for', v.file.name, e);
            survivors.push(v);
            continue;
        }
        if (info?.count == null) {
            survivors.push(v);
            continue;
        }
        const fmt = info.format;
        const mult = (fmt === 'pdf') ? pdfRate : officeRate;
        const creditsNeeded = info.count * mult;
        if (userGroups.length > 0) {
            if (canCoverWithDrawOrder(userGroups, creditsNeeded)) {
                survivors.push(v);
                continue;
            }
        }
        else {
            const balance = (userTokenBalance && typeof userTokenBalance.credits === 'number')
                ? userTokenBalance.credits : null;
            if (balance == null || balance >= UNLIMITED_SENTINEL) {
                survivors.push(v);
                continue;
            }
            if (creditsNeeded <= balance) {
                survivors.push(v);
                continue;
            }
        }
        const chain = computeDrawOrder(userGroups);
        const totalAvail = chain.length > 0
            ? chain.reduce((sum, e) => sum + (e.available >= UNLIMITED_SENTINEL ? 0 : e.available), 0)
            : ((userTokenBalance && typeof userTokenBalance.credits === 'number') ? userTokenBalance.credits : 0);
        // Self-serve purchase was removed with the Stripe strike (invoice-everywhere
        // Beta). The over-quota file is surfaced to the user for acknowledgement, then
        // skipped — there is no top-up path to redirect to.
        await mod.showInsufficientTokensModal({
            filename: v.file.name,
            pages: info.count,
            format: fmt,
            multiplier: mult,
            required: creditsNeeded,
            balance: totalAvail,
            isEstimate: !!info.isEstimate,
        });
        const fmtLabel = (fmt === 'pdf') ? 'PDF' : 'Office';
        if (showBanner) {
            showBanner(`"${v.file.name}" (${info.count} ${fmtLabel} pages = ${creditsNeeded} credits) was skipped.`);
        }
    }
    return survivors;
}
// Wrappers for the OLD-STYLE global-state computeDrawOrder/canCoverWithDrawOrder
// that take no args and read the singleton's groups directly. These mirror
// the legacy app.js shapes that callers + window.__adatoolUserState expose.
/**
 * e2e-test hook. Forwards into `AuthClient.setForTest` on the module
 * singleton — used by `window.__adatoolUserState.setForTest` (consumed by
 * test/e2e/test-token-model*.vitest.mjs). Bypasses /api/auth/me to prime
 * the v2.1 group state in tests.
 */
export function setUserStateForTest(cls, balance, groups) {
    _client_().setForTest(cls, balance, groups);
}
/** Group-aware draw-order resolver using the current AuthClient's groups. */
export function computeDrawOrderFromClient() {
    return computeDrawOrder(_client_().getUserGroups());
}
/** Group-aware sufficiency check using the current AuthClient's groups. */
export function canCoverWithDrawOrderFromClient(needed) {
    return canCoverWithDrawOrder(_client_().getUserGroups(), needed);
}
// ─── Format helper exposed for completeness (legacy badge formatter) ────────
export function formatCreditsBadge(credits) {
    return _formatCreditsBadge(credits);
}
